import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { routes } from '@platform/core/lib/routes';
import { useGetCatalogQuery } from '@platform/state/api';
import { mainRecordTypeOf } from '@platform/engine/menu/moduleSections';
import { useViewLabel } from '@platform/engine/list/useViewLabel';
import type { ISettingsRecordsProps } from './ISettingsRecordsProps';
import { useFormSource } from './useFormSource';
import { useGlobalModuleFacts } from './useGlobalModuleFacts';
import { useRecordsRows } from './useRecordsRows';

/** Подання модуля прототипу v4 (m.views): «Етапи», «Календар», «Панель» — завжди доступні, їх «Подання» не перелічує. */
const MODULE_VIEWS = ['table', 'cards', 'inline', 'backlog', 'split'];
/** Пігулка форми основного типу за джерелом форми (прототип v4 cdForms st): клас і підпис. */
const SOURCE_PILL = { ws: ['st-pill--main', 'workspace'], global: ['st-pill--global', 'global'], std: [undefined, 'standard'] } as const;

/**
 * «Записи й форми» модуля (прототип v4 pgCustModule): «Записи» — основний тип (таблиця БД, кількість полів і записів, кнопка «Поля» —
 * «Налаштування › Конструктор модулів» цього типу з обох контекстів, як builder прототипу з bScoped=false) і сутності прототипу як
 * «Спроєктовано» (пігулка з рамкою); «Форми» — форма основного типу (стандартний макет чи форма простору, «Редагувати форму» — редактор
 * у тому самому контексті, `links.formTo`) і спроєктовані форми сутностей (пігулка без рамки, як у прототипі); «Подання» — подання
 * модуля. Підсвічено рядок сутності з `?focus=` (пункти «Таблиці» та довідників меню). Дочірня таблиця (`rt`, окремий модуль прототипу:
 * «Видатки») — її записи, форма й подання як основного типу сторінки. На рівні «Глобально» (ADR-027) — записи в усіх робочих просторах,
 * без «Поля» (конструктор лише в просторі), пігулка форми — «Глобально» чи «Стандартний»; у просторі — ще й успадкована «Глобально».
 */
export function SettingsRecords({ ws, module, rt, focus, links, global }: ISettingsRecordsProps) {
  const { t } = useTranslation();
  const viewLabel = useViewLabel(module);
  const rows = useRecordsRows(module, rt);
  const main = rt ?? mainRecordTypeOf(module);
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws || global });
  const [pillClass, pillKey] = SOURCE_PILL[useFormSource(ws, module.module.key, main, global)];
  const facts = useGlobalModuleFacts(module.module.key, main?.key, !global);
  const count = global ? facts.recordCount ?? 0
    : catalog?.modules.find((m) => m.key === module.module.key)?.recordTypes.find((r) => r.key === main?.key)?.recordCount ?? 0;
  const mainMeta = (r: NonNullable<typeof main>) => {
    const fields = r.fields.filter((f) => !f.hidden).length;
    return global ? t('modSettings.records.mainMetaGlobal', { table: r.dbTable, fields, count }) : t('modSettings.records.mainMeta', { table: r.dbTable, fields, records: count });
  };
  const designed = <span className="st-pill st-pill--designed">{t('modSettings.records.designed')}</span>;
  return (
    <>
      <div className="st-rf">
        <section className="st-rf__card">
          <div className="st-rf__label"><span className="st-kicker">{t('modSettings.records.records')}</span><span>{t('modSettings.records.recordsSub')}</span></div>
          <div className="st-rf__list">{rows.map((row) => (
            <div key={row.key} className={cx('st-rf__row', !row.main && focus && row.focus.includes(focus) && 'is-focus')}>
              <div className="st-rf__text">
                <span className="st-rf__name"><strong>{row.name}</strong>{row.main ? <span className="st-pill st-pill--main">{t('modSettings.records.main')}</span> : designed}</span>
                <span className="st-rf__desc">{row.main && main ? mainMeta(main) : row.description}</span>
              </div>
              {row.main && main && !global && <Link className="st-btn st-btn--accent" to={routes.customize('builder', main.key)}>{t('modSettings.records.fieldsBtn')}</Link>}
            </div>
          ))}</div>
        </section>
        <section className="st-rf__card">
          <div className="st-rf__label"><span className="st-kicker">{t('modSettings.records.forms')}</span><span>{t('modSettings.records.formsSub')}</span></div>
          <div className="st-rf__list">{rows.map((row) => (
            <div key={row.key} className="st-rf__row st-rf__row--flat">
              <div className="st-rf__text">
                <span className="st-rf__name">
                  <strong>{t('modSettings.records.formName', { name: row.name })}</strong>
                  <span className="st-rf__kind">{t(row.main ? 'modSettings.records.mainForm' : 'modSettings.records.recordForm')}</span>
                  <span className={cx('st-pill', row.main && pillClass)}>{t(row.main ? `modSettings.records.${pillKey}` : 'modSettings.records.designed')}</span>
                </span>
                <span className="st-rf__desc">{row.formDescription}</span>
              </div>
              {row.main && <Link className="st-btn st-btn--accent" to={links.formTo}>{t('modSettings.records.editForm')}</Link>}
            </div>
          ))}</div>
        </section>
      </div>
      <section className="st-rf__views">
        <div className="st-rf__label"><span className="st-kicker">{t('modSettings.records.views')}</span><span>{t('modSettings.records.viewsSub')}</span></div>
        <div className="st-rf__chips">{(main?.views.enabled ?? []).filter((v) => MODULE_VIEWS.includes(v)).map((v) => <span key={v}>{viewLabel(v)}</span>)}</div>
      </section>
    </>
  );
}
